import React from 'react';
import { Button, Form } from 'reactstrap';
import { httpsCallable } from 'firebase/functions';
import { doc, collection, updateDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { loadStripe } from '@stripe/stripe-js';
import { useToggle } from 'react-use';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import { mapValues } from 'lodash';

import firebase, { functions, db } from '../firebase';
import logo from '../assets/logo.png';
import env from '../env';
import useUser from './hooks/useUser';
import useDocumentSubscription from './hooks/useDocumentSubscription';
import useFormState from './hooks/useFormState';
import Field from './Field';
import { fieldsToActivate } from '../shared/models/user';

const { entries } = Object;
const auth = getAuth(firebase);

export default function SubscriptionGate () {
  const { user } = useUser();
  const tenant = useDocumentSubscription(user && doc(collection(db, 'tenants'), user.tenantId), [user]);

  if (!user || !tenant) return null;

  return <SubscriptionGateForm user={user} tenant={tenant} />;
}

function SubscriptionGateForm ({ user, tenant }) {
  const [isLoading, toggleLoading] = useToggle(false);
  const canGoBackToDemo = tenant?.hasDemo;
  const isResuming = !!tenant.subscriptionId;
  const statedFields = useFormState({ ...user, tenantName: tenant.name }, fieldsToActivate);
  const isUnsubmittable = Object.values(statedFields).some(_ => !_.isValid);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (isUnsubmittable) return;

    toggleLoading(true);
    try {
      const values = mapValues(statedFields, 'value');

      await Promise.all([
        updateDoc(doc(collection(db, 'users'), user.uid), {
          displayName: values.displayName,
          nameKana: values.nameKana,
          phone: values.phone,
        }),
        updateDoc(doc(collection(db, 'tenants'), user.tenantId), {
          name: values.tenantName,
        }),
        updateProfile(auth.currentUser, { displayName: values.displayName }),
      ]);

      if (isResuming) {
        const createCustomerPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
        const { data: { url } } = await createCustomerPortalSession();
        window.location.href = url;
      } else {
        const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
        const { data: { sessionId } } = await createCheckoutSession({ phone: values.phone });
        const stripe = await loadStripe(env('STRIPE_PUBLIC_KEY'));
        await stripe.redirectToCheckout({ sessionId });
      }
    } catch (e) {
      console.error(e);
      // 既に契約済み/決済済みの場合はサーバ側で弾かれる。再試行ではなく再読み込みが要る
      toast.error(e?.code === 'functions/already-exists'
        ? e.message
        : 'エラーが発生しました。もう一度お試しください。');
      toggleLoading(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-md-8 offset-md-2 col-lg-6 offset-lg-3">
          <div className="d-flex justify-content-center mb-5">
            <img src={logo} alt="Totono" style={{ height: 200 }} />
          </div>
          <div className="card">
            <div className="card-body p-5">
              <h5 className="mb-4 text-center">{isResuming ? 'ご利用再開' : 'ご利用情報の入力'}</h5>
              <Form onSubmit={onSubmit}>
                {entries(statedFields).map(([fieldName, fieldSetting]) => (
                  <Field
                    documentName="user"
                    key={fieldName}
                    name={fieldName}
                    {...fieldSetting}
                  />
                ))}
                <hr className="my-4" />
                <div className="border rounded p-4 mb-4">
                  <div className="fw-bold mb-2 text-center">ライトプラン</div>
                  <div className="fs-3 fw-bold mb-2 text-center">¥3,080<span className="fs-6 fw-normal text-muted"> /月（税込）</span></div>
                  {!isResuming && (
                    <div className="text-muted small text-center">30日間の無料トライアル付き</div>
                  )}
                </div>
                {!isResuming && (
                  <div className="alert alert-warning small mb-3">
                    <div className="fw-bold mb-1">
                      <span className="fas fa-exclamation-triangle me-1" />
                      ご登録時のご注意
                    </div>
                    次の画面でクレジットカードをご登録いただきます。住所欄には<strong>カード会社に届け出ている住所</strong>をご入力ください。現住所と異なる場合（お引越し後で未変更など）、本人認証に失敗することがあります。
                  </div>
                )}
                <Button color="primary" size="lg" className="w-100" type="submit" disabled={isUnsubmittable || isLoading}>
                  {isLoading ? '処理中...' : (isResuming ? 'お支払い情報の管理画面へ' : '無料トライアルを開始する')}
                </Button>
                <p className="text-muted small mt-3 mb-0 text-center">
                  {isResuming
                    ? 'Stripeのお支払い管理画面に移動します。プラン再開や支払い方法の更新が行えます。'
                    : 'トライアル期間中は課金されません。30日後に自動で月額課金が開始されます。'}
                </p>
              </Form>
            </div>
          </div>
          <p className="text-muted small text-center mt-3 d-flex justify-content-center gap-3">
            {canGoBackToDemo && (
              <span onClick={async () => {
                await updateDoc(doc(collection(db, 'tenants'), user.tenantId), { wantsActivation: false });
              }} className="text-primary cursor-pointer">デモに戻る</span>
            )}
            <span onClick={() => auth.signOut()} className="text-primary cursor-pointer">ログアウト</span>
          </p>
        </div>
      </div>
    </div>
  );
}
